/**
 * RecipientAccountsPage — `/settings/recipient-accounts`.
 *
 * Manage NIPOST master, partner and franchise settlement accounts. Backed by
 * the generic settings-collection store on service-audit
 * (`GET / PUT /v1/settings-collections/recipient_accounts`).
 */
import { type JSX, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Pause,
  Plus,
  RefreshCw,
  Save,
  Shield,
  Users,
  Wallet,
  X,
} from 'lucide-react';

import {
  getSettingsCollection,
  putSettingsCollection,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

type Verification =
  | 'VERIFIED'
  | 'PENDING_VERIFICATION'
  | 'REVIEW_REQUIRED'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'SUPERSEDED';

type AccountType = 'NIPOST_MASTER' | 'PARTNER' | 'FRANCHISE';

interface Account {
  readonly id: string;
  readonly type: AccountType;
  readonly label: string;
  readonly ownerLabel?: string;
  readonly bank: string;
  readonly last4: string;
  readonly holder: string;
  readonly verification: Verification;
  readonly verifiedAt: string | null;
}

const COLLECTION_KEY = 'recipient_accounts' as const;

function VerificationBadge({ status }: { status: Verification }): JSX.Element {
  const config: Record<Verification, { className: string; label: string; Icon: typeof CheckCircle2 }> = {
    VERIFIED:             { className: 'bg-success-light text-success', label: 'Verified', Icon: CheckCircle2 },
    PENDING_VERIFICATION: { className: 'bg-amber-50 text-amber-700', label: 'Pending verification', Icon: Clock },
    REVIEW_REQUIRED:      { className: 'bg-amber-100 text-amber-800', label: 'Review required', Icon: AlertTriangle },
    REJECTED:             { className: 'bg-danger-light text-danger', label: 'Rejected', Icon: X },
    SUSPENDED:            { className: 'bg-gray-100 text-gray-700', label: 'Suspended', Icon: Pause },
    SUPERSEDED:           { className: 'bg-gray-50 text-gray-600', label: 'Superseded', Icon: RefreshCw },
  };
  const cfg = config[status];
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded ${cfg.className}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

interface StatTileProps {
  readonly icon: typeof Wallet;
  readonly label: string;
  readonly value: number;
  readonly tint: string;
}

function StatTile({ icon: Icon, label, value, tint }: StatTileProps): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-xl p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-base font-bold text-gray-900 leading-none">{value}</p>
        <p className="text-[10px] text-muted uppercase tracking-wider mt-0.5">{label}</p>
      </div>
    </div>
  );
}

interface SectionProps {
  readonly title: string;
  readonly subtitle: string;
  readonly icon: typeof Shield;
  readonly tint: string;
  readonly accounts: readonly Account[];
}

function Section({ title, subtitle, icon: Icon, tint, accounts }: SectionProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden mb-4">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <div className={`w-7 h-7 rounded flex items-center justify-center ${tint}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div>
          <p className="text-xs font-bold text-gray-900 uppercase tracking-wider">{title}</p>
          <p className="text-[10px] text-muted">{subtitle}</p>
        </div>
      </div>
      {accounts.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted">No accounts</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Recipient</th>
              <th className="p-3 text-left">Bank</th>
              <th className="p-3 text-left">Account</th>
              <th className="p-3 text-left">Holder</th>
              <th className="p-3 text-left">Verification</th>
              <th className="p-3 text-left">Verified at</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className="border-t border-border hover:bg-gray-50">
                <td className="p-3">
                  <p className="font-semibold text-sm text-gray-900">{a.label}</p>
                  {a.ownerLabel ? <p className="text-[10px] text-muted">{a.ownerLabel}</p> : null}
                </td>
                <td className="p-3 text-xs">{a.bank}</td>
                <td className="p-3 text-xs font-mono">****{a.last4}</td>
                <td className="p-3 text-xs">{a.holder}</td>
                <td className="p-3"><VerificationBadge status={a.verification} /></td>
                <td className="p-3 text-xs">{a.verifiedAt ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function RecipientAccountsPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['settings-collection', COLLECTION_KEY],
    queryFn: () => getSettingsCollection<Account>(COLLECTION_KEY),
    retry: false,
    staleTime: 30_000,
  });

  const [accounts, setAccounts] = useState<readonly Account[]>([]);
  const [dirty, setDirty] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Partial<Account>>({
    type: 'FRANCHISE',
    verification: 'PENDING_VERIFICATION',
  });

  useEffect(() => {
    if (query.data && !dirty) {
      setAccounts(query.data.items);
    }
  }, [query.data, dirty]);

  const saveMut = useMutation({
    mutationFn: (next: readonly Account[]) =>
      putSettingsCollection<Account>(COLLECTION_KEY, next),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['settings-collection', COLLECTION_KEY] });
    },
  });

  const stats = useMemo(() => {
    const active = accounts.filter((a) => a.verification === 'VERIFIED').length;
    const pending = accounts.filter(
      (a) => a.verification === 'PENDING_VERIFICATION' || a.verification === 'REVIEW_REQUIRED',
    ).length;
    const failed = accounts.filter((a) => a.verification === 'REJECTED').length;
    const suspended = accounts.filter((a) => a.verification === 'SUSPENDED').length;
    return { active, pending, failed, suspended };
  }, [accounts]);

  const nipostMaster = accounts.filter((a) => a.type === 'NIPOST_MASTER');
  const partnerAccts = accounts.filter((a) => a.type === 'PARTNER');
  const franchiseAccts = accounts.filter(
    (a) => a.type === 'FRANCHISE' && a.label.toLowerCase().includes(filterText.toLowerCase()),
  );

  function addAccount(): void {
    if (!draft.label || !draft.bank || !draft.last4 || !draft.holder) return;
    const id = `rec-${Date.now()}`;
    const next: readonly Account[] = [
      {
        id,
        type: draft.type ?? 'FRANCHISE',
        label: draft.label,
        ownerLabel: draft.ownerLabel ?? '',
        bank: draft.bank,
        last4: draft.last4,
        holder: draft.holder,
        verification: 'PENDING_VERIFICATION',
        verifiedAt: null,
      },
      ...accounts,
    ];
    setAccounts(next);
    setDirty(true);
    setAdding(false);
    setDraft({ type: 'FRANCHISE', verification: 'PENDING_VERIFICATION' });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Recipient accounts"
        description="Every payout target on the platform — NIPOST master, partners, and franchise settlements."
        actions={
          <div className="flex items-center gap-2">
            {dirty ? (
              <button
                type="button"
                onClick={() => saveMut.mutate(accounts)}
                disabled={saveMut.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> {saveMut.isPending ? 'Saving…' : 'Save changes'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add account
            </button>
          </div>
        }
      />

      {query.isPending ? (
        <p className="text-sm text-muted">Loading recipient accounts…</p>
      ) : query.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Could not load accounts: {(query.error as Error).message}
        </div>
      ) : null}

      {saveMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={Wallet} label="Active recipients" value={stats.active} tint="bg-nipost-green-light text-nipost-green" />
        <StatTile icon={Clock} label="Pending verification" value={stats.pending} tint="bg-amber-50 text-amber-700" />
        <StatTile icon={AlertTriangle} label="NIBSS failures" value={stats.failed} tint="bg-danger-light text-danger" />
        <StatTile icon={Pause} label="Suspended" value={stats.suspended} tint="bg-gray-100 text-gray-700" />
      </div>

      <Section
        title="NIPOST Master Account"
        subtitle="Exactly one ACTIVE account at a time."
        icon={Shield}
        tint="bg-nipost-green-light text-nipost-green"
        accounts={nipostMaster}
      />

      <Section
        title="Partner Accounts"
        subtitle="Named partners and collection agents."
        icon={Users}
        tint="bg-purple-50 text-purple-700"
        accounts={partnerAccts}
      />

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded flex items-center justify-center bg-amber-50 text-amber-700">
              <Building2 className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                Franchise Settlement Accounts
              </p>
              <p className="text-[10px] text-muted">One active account per franchise.</p>
            </div>
          </div>
          <input
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter franchises…"
            aria-label="Filter franchises"
            className="text-xs px-2 py-1 border border-border rounded"
          />
        </div>
        {franchiseAccts.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-muted">No accounts</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Recipient</th>
                <th className="p-3 text-left">Bank</th>
                <th className="p-3 text-left">Account</th>
                <th className="p-3 text-left">Holder</th>
                <th className="p-3 text-left">Verification</th>
              </tr>
            </thead>
            <tbody>
              {franchiseAccts.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-gray-50">
                  <td className="p-3">
                    <p className="font-semibold text-sm text-gray-900">{a.label}</p>
                    <p className="text-[10px] text-muted">{a.ownerLabel}</p>
                  </td>
                  <td className="p-3 text-xs">{a.bank}</td>
                  <td className="p-3 text-xs font-mono">****{a.last4}</td>
                  <td className="p-3 text-xs">{a.holder}</td>
                  <td className="p-3"><VerificationBadge status={a.verification} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {adding ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="text-base font-bold text-gray-900">Add recipient account</p>
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="p-1.5 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted tracking-wider">Type</label>
                <select
                  value={draft.type}
                  aria-label="Account type"
                  onChange={(e) => setDraft({ ...draft, type: e.target.value as AccountType })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                >
                  <option value="FRANCHISE">Franchise settlement</option>
                  <option value="PARTNER">Partner</option>
                  <option value="NIPOST_MASTER">NIPOST master</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted tracking-wider">Label</label>
                <input
                  type="text"
                  value={draft.label ?? ''}
                  aria-label="Label"
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted tracking-wider">Owner label</label>
                <input
                  type="text"
                  value={draft.ownerLabel ?? ''}
                  aria-label="Owner label"
                  onChange={(e) => setDraft({ ...draft, ownerLabel: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold uppercase text-muted tracking-wider">Bank</label>
                  <input
                    type="text"
                    value={draft.bank ?? ''}
                    aria-label="Bank"
                    onChange={(e) => setDraft({ ...draft, bank: e.target.value })}
                    className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase text-muted tracking-wider">Last 4</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={draft.last4 ?? ''}
                    aria-label="Last 4"
                    onChange={(e) => setDraft({ ...draft, last4: e.target.value })}
                    className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted tracking-wider">Account holder</label>
                <input
                  type="text"
                  value={draft.holder ?? ''}
                  aria-label="Holder"
                  onChange={(e) => setDraft({ ...draft, holder: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg uppercase"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addAccount}
                className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg"
              >
                Add account
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
